/**
 * Evolu database schema for Satsflow Wallet.
 *
 * Defines all 16 tables from DATA_SCHEMA.md using Evolu's branded type system.
 * Tables for future phases (messages, groups, expenses, recovery) are defined
 * upfront because Evolu uses additive migrations -- defining them early costs
 * nothing and prevents migration complexity later.
 *
 * System columns (id, createdAt, updatedAt, isDeleted) are added automatically
 * by Evolu and must NOT be defined in the schema.
 *
 * @module db/schema
 */

import { NonEmptyString1000, nullOr, String, Number, SqliteBoolean } from '@evolu/common';
import type { EvoluSchema } from '@evolu/common';
import { TransactionTable } from './tables/transaction-table';

// ── Branded ID Types (from schema-ids.ts) ────────────────────────────────────

import {
  WalletConnectionId,
  CashuMintId,
  CashuTokenId,
  CashuCounterId,
  ContactId,
  CategoryId,
  SettingId,
  UserIdentityId,
  MessageId,
  GroupId,
  GroupMemberId,
  ExpenseId,
  GroupJarId,
  RecoveryConfigId,
  RecoveryClaimId,
} from './schema-ids';

export {
  WalletConnectionId,
  CashuMintId,
  CashuTokenId,
  CashuCounterId,
  TransactionId,
  ContactId,
  CategoryId,
  SettingId,
  UserIdentityId,
  MessageId,
  GroupId,
  GroupMemberId,
  ExpenseId,
  GroupJarId,
  RecoveryConfigId,
  RecoveryClaimId,
} from './schema-ids';

// ── Schema Definition ─────────────────────────────────────────────────────────

/**
 * Complete Evolu database schema for Satsflow Wallet.
 *
 * All tables from DATA_SCHEMA.md are defined here. Nullable columns use
 * `nullOr()` which makes them optional in inserts. Evolu auto-manages
 * id, createdAt, updatedAt, and isDeleted columns.
 */
export const Schema = {
  /** NWC connection strings and metadata for connected wallets. */
  walletConnection: {
    id: WalletConnectionId,
    type: NonEmptyString1000,
    label: NonEmptyString1000,
    connectionSecret: NonEmptyString1000,
    pubkey: NonEmptyString1000,
    relayUrl: NonEmptyString1000,
    isActive: SqliteBoolean,
    lastConnectedAt: nullOr(String),
    lastKnownBalanceSats: nullOr(Number),
    capabilities: nullOr(String),
  },

  /** Connected Cashu mint information. */
  cashuMint: {
    id: CashuMintId,
    url: NonEmptyString1000,
    label: NonEmptyString1000,
    keysets: nullOr(String),
    trustLevel: NonEmptyString1000,
    isActive: SqliteBoolean,
    lastSyncedAt: nullOr(String),
    info: nullOr(String),
  },

  /** Cashu ecash proofs (bearer tokens). Critical: these are money. */
  cashuToken: {
    id: CashuTokenId,
    mintId: NonEmptyString1000,
    amount: Number,
    secret: NonEmptyString1000,
    c: NonEmptyString1000,
    keysetId: NonEmptyString1000,
    state: NonEmptyString1000,
    lockedTo: nullOr(String),
    spentAt: nullOr(String),
    spentInTxId: nullOr(String),
    derivation: nullOr(String),
    derivationCounter: nullOr(Number),
  },

  /** NUT-13 deterministic secret counter state per keyset. */
  cashuCounter: {
    id: CashuCounterId,
    keysetId: NonEmptyString1000,
    mintUrl: NonEmptyString1000,
    nextCounter: Number,
  },

  /** Unified transaction log across all payment methods. */
  transaction: TransactionTable,

  /** Address book with multi-protocol support. */
  contact: {
    id: ContactId,
    name: NonEmptyString1000,
    avatarUrl: nullOr(String),
    lightningAddress: nullOr(String),
    bolt12Offer: nullOr(String),
    cashuAddress: nullOr(String),
    nostrPubkey: nullOr(String),
    notes: nullOr(String),
    isFavorite: SqliteBoolean,
    lastTransactionAt: nullOr(String),
    transactionCount: Number,
    isVerified: SqliteBoolean,
    verifiedAt: nullOr(String),
    verificationHash: nullOr(String),
    verificationMethod: nullOr(String),
    isRecoveryContact: SqliteBoolean,
    recoveryAddedAt: nullOr(String),
    trustLevel: NonEmptyString1000,
  },

  /** User-defined spending categories. */
  category: {
    id: CategoryId,
    name: NonEmptyString1000,
    color: NonEmptyString1000,
    icon: nullOr(String),
    budget: nullOr(Number),
    isDefault: SqliteBoolean,
    sortOrder: Number,
  },

  /** Key-value store for app configuration. */
  setting: {
    id: SettingId,
    key: NonEmptyString1000,
    value: NonEmptyString1000,
  },

  /** User's Nostr keypair and shareable payment profile. */
  userIdentity: {
    id: UserIdentityId,
    nostrPrivkey: NonEmptyString1000,
    nostrPubkey: NonEmptyString1000,
    derivationPath: NonEmptyString1000,
    displayName: nullOr(String),
    lightningAddress: nullOr(String),
    bolt12Offer: nullOr(String),
    cashuAddress: nullOr(String),
  },

  /** Decrypted messages for conversations (payment-context initially). */
  message: {
    id: MessageId,
    nostrEventId: NonEmptyString1000,
    conversationId: NonEmptyString1000,
    senderPubkey: NonEmptyString1000,
    recipientPubkey: nullOr(String),
    groupId: nullOr(String),
    contentType: NonEmptyString1000,
    content: NonEmptyString1000,
    paymentData: nullOr(String),
    relayUrl: NonEmptyString1000,
    nostrCreatedAt: NonEmptyString1000,
    isRead: SqliteBoolean,
    isOutgoing: SqliteBoolean,
  },

  /** Group metadata for expense splitting. */
  group: {
    id: GroupId,
    name: NonEmptyString1000,
    nostrGroupEventId: nullOr(String),
    type: NonEmptyString1000,
    memberCount: Number,
    createdByPubkey: NonEmptyString1000,
    isActive: SqliteBoolean,
    avatarUrl: nullOr(String),
    relayUrl: NonEmptyString1000,
  },

  /** Membership, roles, and running balances within a group. */
  groupMember: {
    id: GroupMemberId,
    groupId: NonEmptyString1000,
    contactId: nullOr(String),
    nostrPubkey: NonEmptyString1000,
    displayName: NonEmptyString1000,
    role: NonEmptyString1000,
    joinedAt: NonEmptyString1000,
    isVerified: SqliteBoolean,
    balance: Number,
  },

  /** Group expenses with split tracking. */
  expense: {
    id: ExpenseId,
    groupId: NonEmptyString1000,
    description: NonEmptyString1000,
    totalAmountSats: Number,
    paidByPubkey: NonEmptyString1000,
    paidByContactId: nullOr(String),
    splitType: NonEmptyString1000,
    splits: NonEmptyString1000,
    categoryId: nullOr(String),
    nostrEventId: nullOr(String),
    status: NonEmptyString1000,
    expenseDate: NonEmptyString1000,
  },

  /** Shared Cashu token pool for a group. */
  groupJar: {
    id: GroupJarId,
    groupId: NonEmptyString1000,
    mintId: NonEmptyString1000,
    totalAmountSats: Number,
    tokenIds: NonEmptyString1000,
    contributorLog: NonEmptyString1000,
  },

  /** Configuration for recovery contacts and inheritance. */
  recoveryConfig: {
    id: RecoveryConfigId,
    type: NonEmptyString1000,
    contactId: NonEmptyString1000,
    contactPubkey: NonEmptyString1000,
    encryptedPayload: NonEmptyString1000,
    encryptionMethod: NonEmptyString1000,
    delayDays: Number,
    isActive: SqliteBoolean,
    lastVerifiedAt: nullOr(String),
    payloadContents: NonEmptyString1000,
    lastPayloadUpdateAt: nullOr(String),
  },

  /** Tracks the delay-and-notify lifecycle for recovery claims. */
  recoveryClaim: {
    id: RecoveryClaimId,
    recoveryConfigId: NonEmptyString1000,
    claimantPubkey: NonEmptyString1000,
    nostrEventId: NonEmptyString1000,
    claimInitiatedAt: NonEmptyString1000,
    delayExpiresAt: NonEmptyString1000,
    status: NonEmptyString1000,
    ownerNotifiedAt: nullOr(String),
    ownerResponseAt: nullOr(String),
    completedAt: nullOr(String),
  },
} as const satisfies EvoluSchema;

/**
 * The number of tables defined in the schema.
 * Used for schema completeness verification in tests.
 */
export const TABLE_COUNT = 16;

/**
 * All table names in the schema.
 */
export const TABLE_NAMES = Object.keys(Schema) as ReadonlyArray<keyof typeof Schema>;
